import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogHeader as AlertHdr,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { useTRPC } from '@/trpc/react'
import { EMPLOYEE_SETTINGS_FIELD_GROUP_LABELS, EMPLOYEE_SETTINGS_FIELD_GROUPS } from '@repo/abac'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ALL_RESOURCES, getResourceLabel } from './-common/common'

export const Route = createFileRoute('/dashboard/admin/role_permissions/hr-admin')({
  component: RouteComponent,
})

const toFilter = ['Permissions']

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const roleName = 'hr_admin' as const

  const filteredResources = ALL_RESOURCES.filter(
    (resource) => !toFilter.includes(resource.resource),
  )

  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>(() => {
    const initial: Record<string, Record<string, boolean>> = {}
    for (const def of filteredResources) {
      initial[def.resource] = {}
      for (const action of def.actions) initial[def.resource][action] = false
    }
    return initial
  })

  type FieldName = (typeof EMPLOYEE_SETTINGS_FIELD_GROUPS)[number]
  type ActionName = 'read' | 'create' | 'update'
  const [employeeSettingsFields, setEmployeeSettingsFields] = useState<
    Record<ActionName, Record<FieldName, boolean>>
  >(() => ({
    read: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
    create: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
    update: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
  }))

  function toggle(resource: string, action: string): void {
    setMatrix((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: !prev[resource]?.[action],
      },
    }))
  }

  const { mutateAsync: saveRole, isPending } = useMutation({
    ...api.permissions.saveRolePermissions.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: api.user.getUsers.queryKey() })
        toast.success('Permissions saved')
      },
      onError: (error: unknown) => {
        console.error(error)
        toast.error('Failed to save permissions')
      },
    }),
  })

  const rolePermsQuery = useQuery({
    ...api.permissions.getRolePermissions.queryOptions({ roleName }),
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    const rolePerms = rolePermsQuery.data
    if (!rolePerms) return
    const next: Record<string, Record<string, boolean>> = {}
    for (const def of filteredResources) {
      next[def.resource] = {}
      for (const action of def.actions) next[def.resource][action] = false
    }
    const nextFields: Record<ActionName, Record<FieldName, boolean>> = {
      read: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
      create: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
      update: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
    }
    for (const p of rolePerms) {
      if (!next[p.resource]) next[p.resource] = {}
      next[p.resource][p.action] = p.effect === 'allow'
      if (
        p.resource === 'EmployeeSettings' &&
        (p.action === 'read' || p.action === 'create' || p.action === 'update') &&
        Array.isArray(p.fields)
      ) {
        for (const fld of p.fields) {
          if ((EMPLOYEE_SETTINGS_FIELD_GROUPS as readonly string[]).includes(fld as string)) {
            nextFields[p.action as ActionName][fld as FieldName] = true
          }
        }
      }
    }
    if (JSON.stringify(matrix) !== JSON.stringify(next)) setMatrix(next)
    if (JSON.stringify(employeeSettingsFields) !== JSON.stringify(nextFields))
      setEmployeeSettingsFields(nextFields)
  }, [rolePermsQuery.data])

  function handleSave(): void {
    const permissions = Object.entries(matrix).flatMap(([resource, actions]) =>
      Object.entries(actions)
        .filter(([, enabled]) => enabled)
        .map(([action]) => {
          if (
            resource === 'EmployeeSettings' &&
            (action === 'read' || action === 'create' || action === 'update')
          ) {
            const selected = Object.entries(employeeSettingsFields[action as ActionName])
              .filter(([, on]) => on)
              .map(([f]) => f)
            return selected.length > 0
              ? ({ resource, action, effect: 'allow' as const, fields: selected } as const)
              : ({ resource, action, effect: 'allow' as const } as const)
          }
          return { resource, action, effect: 'allow' as const }
        }),
    )

    void saveRole({ roleName, permissions })
  }

  return (
    <DashboardShell>
      <div className="mb-2 flex items-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/dashboard/admin/role_permissions' })}
        >
          <ChevronLeft className="mr-1" />
          Back
        </Button>
      </div>
      <DashboardHeader heading="Role Permissions" text="Configure role permissions" />
      <Separator />
      <div className="text-sm text-muted-foreground mt-3">
        <span className="font-medium">Note:</span> Enabling &quot;manage&quot; grants read, create,
        update, and delete for that specific resource.
      </div>

      <div className="space-y-4 mt-4">
        <div className="text-3xl font-semibold">hr_admin</div>
        {filteredResources.map((def) => (
          <div key={def.resource} className="space-y-2 border p-2 rounded-md">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{getResourceLabel(def.resource)}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {def.actions.map((action) => {
                const checked = !!matrix?.[def.resource]?.[action]
                const id = `${roleName}-${def.resource}-${action}`
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between rounded-md border p-2"
                  >
                    <Label htmlFor={id} className="text-sm">
                      {action}
                    </Label>
                    <Switch
                      id={id}
                      checked={checked}
                      onCheckedChange={() => toggle(def.resource, action)}
                      disabled={isPending || rolePermsQuery.isPending}
                    />
                    {def.resource === 'EmployeeSettings' &&
                      (action === 'read' || action === 'create' || action === 'update') && (
                        <div className="col-span-2 mt-2 grid grid-cols-2 gap-2">
                          {EMPLOYEE_SETTINGS_FIELD_GROUPS.map((fld) => {
                            const fldId = `${id}-${fld}`
                            const on = employeeSettingsFields[action as ActionName][fld]
                            const disableFields = !checked || !!matrix?.EmployeeSettings?.manage
                            return (
                              <div
                                key={fldId}
                                className="flex items-center justify-between rounded-md border p-2"
                              >
                                <Label htmlFor={fldId} className="text-xs">
                                  {EMPLOYEE_SETTINGS_FIELD_GROUP_LABELS[fld]}
                                </Label>
                                <Switch
                                  id={fldId}
                                  checked={on}
                                  onCheckedChange={() =>
                                    setEmployeeSettingsFields((prev) => ({
                                      ...prev,
                                      [action as ActionName]: {
                                        ...prev[action as ActionName],
                                        [fld]: !on,
                                      },
                                    }))
                                  }
                                  disabled={isPending || rolePermsQuery.isPending || disableFields}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button disabled={isPending || rolePermsQuery.isPending} className="w-fit mt-4">
            Save
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertHdr>
            <AlertDialogTitle>Confirm Save</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save these permission changes for the hr_admin role? This
              may affect user access across the system.
            </AlertDialogDescription>
          </AlertHdr>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave} disabled={isPending}>
              Save Permissions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  )
}
